/**
 * AdminAdsPage — Manage banner creatives across all slots.
 *
 * /admin/ads
 *   - Group by slot
 *   - Add / Edit / Delete creatives
 *   - View impression / click counters + CTR
 */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Edit3, BarChart3, Eye, ChevronDown, Upload } from "lucide-react";
import api, { resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";

const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm";

export default function AdminAdsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [slots, setSlots] = useState([]);
    const [creatives, setCreatives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // creative object or { slot } for new

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        Promise.all([
            api.get("/admin/ads/slots").then((r) => setSlots(r.data.slots || [])),
            api.get("/admin/ads/creatives").then((r) => setCreatives(r.data || [])),
        ]).finally(() => setLoading(false));
    }, [user, authLoading]); // eslint-disable-line

    const reload = async () => {
        const r = await api.get("/admin/ads/creatives");
        setCreatives(r.data || []);
    };

    const handleSave = async (data) => {
        if (data.id) {
            await api.put(`/admin/ads/creatives/${data.id}`, data);
        } else {
            await api.post("/admin/ads/creatives", data);
        }
        setEditing(null);
        reload();
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Bu reklamı silmek istediğinize emin misiniz?")) return;
        await api.delete(`/admin/ads/creatives/${id}`);
        reload();
    };

    return (
        <AdminLayout title="Reklamlar & Banner Yönetimi">
            {loading ? <div>Yükleniyor...</div> : (
                <div className="space-y-6">
                    {slots.map((slot) => {
                        const slotAds = creatives.filter((c) => c.slot === slot.key);
                        const totalImpressions = slotAds.reduce((s, a) => s + (a.impressions || 0), 0);
                        const totalClicks = slotAds.reduce((s, a) => s + (a.clicks || 0), 0);
                        const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : "0.00";
                        return (
                            <div key={slot.key} data-testid={`slot-${slot.key}`} className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                                <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
                                    <div>
                                        <h3 className="font-display font-bold text-base text-slate-900 dark:text-white">{slot.label}</h3>
                                        <div className="text-xs font-mono text-slate-500 mt-0.5">{slot.key}</div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-xs text-slate-500"><Eye className="inline h-3 w-3 mr-1" />{totalImpressions} gösterim</div>
                                        <div className="text-xs text-slate-500"><BarChart3 className="inline h-3 w-3 mr-1" />{totalClicks} tık · CTR {ctr}%</div>
                                        <button data-testid={`add-creative-${slot.key}`} onClick={() => setEditing({ slot: slot.key })} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold"><Plus className="h-3.5 w-3.5" /> Yeni Banner</button>
                                    </div>
                                </div>
                                {slotAds.length === 0 ? (
                                    <div className="text-xs italic text-slate-400 py-4 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">Henüz bu slota banner yok.</div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {slotAds.map((c) => {
                                            const cCtr = (c.impressions || 0) > 0 ? (((c.clicks || 0) / c.impressions) * 100).toFixed(2) : "0.00";
                                            return (
                                                <div key={c.id} data-testid={`creative-${c.id}`} className={`p-3 rounded-xl border-2 ${c.enabled ? "border-slate-200 dark:border-slate-700" : "border-dashed border-slate-300 dark:border-slate-600 opacity-60"} bg-slate-50 dark:bg-slate-800/30`}>
                                                    {c.image_url && <img src={resolveImageUrl(c.image_url)} alt="" className="w-full h-24 object-cover rounded-lg mb-2" />}
                                                    <div className="font-semibold text-sm text-slate-900 dark:text-white truncate">{c.title || "(başlıksız)"}</div>
                                                    {c.subtitle && <div className="text-xs text-slate-500 truncate">{c.subtitle}</div>}
                                                    <div className="text-[10px] text-slate-400 mt-1">Ağırlık: {c.weight} · {c.impressions || 0} gösterim · {c.clicks || 0} tık · CTR {cCtr}%</div>
                                                    {(c.start_date || c.end_date) && <div className="text-[10px] text-amber-600">📅 {c.start_date || "?"} → {c.end_date || "∞"}</div>}
                                                    <div className="flex gap-1 mt-2">
                                                        <button onClick={() => setEditing(c)} className="flex-1 px-2 py-1 rounded bg-sky-50 dark:bg-sky-950/40 text-sky-600 text-xs font-semibold hover:bg-sky-100"><Edit3 className="h-3 w-3 inline mr-1" />Düzenle</button>
                                                        <button onClick={() => handleDelete(c.id)} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"><Trash2 className="h-3 w-3" /></button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {editing && (
                <CreativeEditor
                    creative={editing}
                    slots={slots}
                    onClose={() => setEditing(null)}
                    onSave={handleSave}
                />
            )}
        </AdminLayout>
    );
}

function CreativeEditor({ creative, slots, onClose, onSave }) {
    const [data, setData] = useState({
        slot: creative.slot || slots[0]?.key || "",
        title: creative.title || "",
        subtitle: creative.subtitle || "",
        image_url: creative.image_url || "",
        mobile_image_url: creative.mobile_image_url || "",
        cta_label: creative.cta_label || "",
        cta_url: creative.cta_url || "",
        bg_color: creative.bg_color || "",
        weight: creative.weight ?? 1,
        enabled: creative.enabled ?? true,
        start_date: creative.start_date || "",
        end_date: creative.end_date || "",
        id: creative.id,
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setData({ ...data, [k]: v });

    const save = async () => {
        setSaving(true);
        try { await onSave(data); }
        catch (e) { alert(e.response?.data?.detail || "Kayıt başarısız"); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-900">
                    <h3 className="font-display text-lg font-bold">{data.id ? "Banner Düzenle" : "Yeni Banner"}</h3>
                    <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800">✕</button>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Slot">
                        <select data-testid="cr-slot" value={data.slot} onChange={(e) => set("slot", e.target.value)} className={INPUT}>
                            {slots.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Başlık (büyük)"><input data-testid="cr-title" value={data.title} onChange={(e) => set("title", e.target.value)} className={INPUT} /></Field>
                    <Field label="Alt başlık"><input value={data.subtitle} onChange={(e) => set("subtitle", e.target.value)} className={INPUT} /></Field>
                    <ImgField label="Görsel (desktop)" value={data.image_url} onChange={(v) => set("image_url", v)} />
                    <ImgField label="Görsel (mobil — ops.)" value={data.mobile_image_url} onChange={(v) => set("mobile_image_url", v)} />
                    <Field label="CTA buton metni"><input value={data.cta_label} onChange={(e) => set("cta_label", e.target.value)} className={INPUT} /></Field>
                    <Field label="CTA URL"><input data-testid="cr-url" value={data.cta_url} onChange={(e) => set("cta_url", e.target.value)} className={INPUT} placeholder="/p/yaz-2026 veya https://..." /></Field>
                    <Field label="Arka plan (görselsiz banner için CSS gradient)"><input value={data.bg_color} onChange={(e) => set("bg_color", e.target.value)} placeholder="linear-gradient(135deg, #0ea5e9, #f97316)" className={`${INPUT} text-xs font-mono`} /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Başlangıç tarihi (ops.)"><input type="date" value={data.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className={INPUT} /></Field>
                        <Field label="Bitiş tarihi (ops.)"><input type="date" value={data.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className={INPUT} /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Ağırlık (1-100, A/B rotasyon)"><input type="number" min="1" max="100" value={data.weight} onChange={(e) => set("weight", parseInt(e.target.value, 10) || 1)} className={INPUT} /></Field>
                        <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!data.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 accent-sky-500" /> <span className="text-sm font-semibold">Aktif</span></label>
                    </div>
                </div>
                <div className="p-5 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 sticky bottom-0 bg-white dark:bg-slate-900">
                    <button onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 text-sm font-bold">İptal</button>
                    <button data-testid="cr-save" onClick={save} disabled={saving} className="px-5 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold disabled:opacity-50">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><span className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>{children}</label>;
}
function ImgField({ label, value, onChange }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const r = await api.post("/admin/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(r.data.url);
        } catch (e) { alert(e.response?.data?.detail || "Yükleme başarısız"); }
        finally { setUploading(false); }
    };
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
            <div className="flex gap-2">
                <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://..." className={`${INPUT} flex-1 text-xs font-mono`} />
                <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="px-3 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 text-xs font-bold whitespace-nowrap disabled:opacity-50"><Upload className="h-3.5 w-3.5 inline mr-1" />{uploading ? "..." : "Yükle"}</button>
                <input ref={inputRef} type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} className="hidden" />
            </div>
            {value && <img src={resolveImageUrl(value)} alt="" className="mt-2 w-full h-20 object-cover rounded-lg" />}
        </div>
    );
}
